/**
 * Nach einer neuen Fassung der App sehen – von allein und auf Knopfdruck.
 *
 * Der Browser prüft das von sich aus nur beim Laden der Seite. In einem
 * Browser-Tab passiert das ständig – als installierte App aber praktisch nie:
 * Die liegt tage- oder wochenlang im App-Umschalter und wird nur ein- und
 * ausgeblendet, ohne je neu zu laden. Genau so kam es dazu, dass eine behobene
 * Sache im Browser sichtbar war und in der installierten App nicht.
 *
 * Eigenes Modul und nicht in `main.tsx`, weil sonst ein Kreis entstünde:
 * `main` rendert `App`, `App` enthält die Einstellungen, und die brauchen den
 * Knopf. ESM kommt damit zurecht, aber es ist die Art von Abhängigkeit, die
 * beim nächsten Umbau jemandem auf die Füsse fällt.
 */

let anmeldung: ServiceWorkerRegistration | null = null;

export function anmeldungMerken(registration: ServiceWorkerRegistration): void {
  anmeldung = registration;
}

/**
 * Regelmässig nachsehen. Zwei Anlässe, und der zweite ist der wichtigere:
 *
 * 1. Alle 30 Minuten, solange die App vorn ist.
 * 2. Immer dann, wenn sie wieder nach vorn geholt wird. Das ist der Moment, in
 *    dem jemand sie benutzen will – und der einzige, den eine installierte App
 *    zuverlässig erlebt.
 *
 * Beides fragt nur nach; ob wirklich neu geladen wird, entscheidet weiterhin
 * der Anwender über das Band oben. Ein Chat, in den gerade jemand tippt, soll
 * nicht unter den Fingern verschwinden.
 */
export function nachNeuerFassungSehen(registration: ServiceWorkerRegistration): void {
  const ABSTAND = 30 * 60 * 1000;
  let zuletzt = Date.now();

  const nachsehen = () => {
    zuletzt = Date.now();
    // Ohne Netz scheitert das – dann eben beim nächsten Anlass.
    void registration.update().catch(() => {});
  };

  window.setInterval(nachsehen, ABSTAND);

  document.addEventListener('visibilitychange', () => {
    // Nicht bei jedem Umschalten: Wer zwischen zwei Apps hin- und herwischt,
    // soll keine Anfrage je Wisch auslösen.
    if (document.visibilityState === 'visible' && Date.now() - zuletzt > 60_000) nachsehen();
  });

  window.addEventListener('online', nachsehen);
}

/**
 * Von Hand nachsehen.
 *
 * Das automatische Nachsehen oben reicht im Alltag – aber nicht, wenn man
 * gerade auf eine bestimmte Änderung wartet und wissen will, ob sie schon da
 * ist. Dann will man einen Knopf und keine halbe Stunde Geduld.
 *
 * Rückgabe: `'neu'`, wenn etwas zum Aktualisieren bereitliegt, `'aktuell'`,
 * wenn nachgesehen wurde und nichts da ist, `'laedt'`, wenn eine neue Fassung
 * gefunden ist und gerade noch geholt wird, und `'unmoeglich'`, wenn gar nicht
 * nachgesehen werden konnte. Das Anwenden macht weiterhin das Band oben – hier
 * wird nur gefragt.
 *
 * Die drei Fälle mussten auseinander: Ohne Service-Worker-Anmeldung – im
 * Entwicklungsbetrieb, im privaten Fenster, bei abgeschalteten Arbeitern – kam
 * schlicht `false` zurück, und der Aufrufer machte daraus die Erfolgsmeldung
 * „Du hast schon den neuesten Stand“. Das ist eine Auskunft über etwas, das
 * nie nachgesehen wurde.
 */
export type UpdateStand = 'neu' | 'aktuell' | 'laedt' | 'unmoeglich';

export async function nachUpdateSuchen(): Promise<UpdateStand> {
  if (!anmeldung) return 'unmoeglich';
  await anmeldung.update();

  // `update()` kommt zurück, sobald der Browser die Datei geholt hat. Ein
  // gefundener neuer Arbeiter braucht danach noch einen Moment, bis er von
  // `installing` auf `waiting` steht – ohne dieses Warten meldete der Knopf
  // „nichts Neues“, und eine Sekunde später erschiene das Band.
  if (anmeldung.waiting) return 'neu';
  const neuer = anmeldung.installing;
  if (!neuer) return 'aktuell';

  return new Promise<UpdateStand>((fertig) => {
    const aufraeumen = () => neuer.removeEventListener('statechange', horcher);
    const horcher = () => {
      if (neuer.state === 'installed') {
        aufraeumen();
        fertig('neu');
      } else if (neuer.state === 'redundant') {
        // Passiert, wenn die Installation scheitert – etwa weil das Netz
        // mitten im Laden wegbricht. Es lag also durchaus etwas bereit; wir
        // konnten es nur nicht holen. „Du hast den neuesten Stand" wäre hier
        // schlicht gelogen.
        aufraeumen();
        fertig('unmoeglich');
      }
    };
    neuer.addEventListener('statechange', horcher);

    /*
     * Nicht ewig hängen bleiben – aber auch nicht „alles aktuell" behaupten.
     *
     * Hier stand `waiting != null ? 'neu' : 'aktuell'`. Dabei ist an dieser
     * Stelle bereits BEWIESEN, dass es eine neue Fassung gibt: `installing`
     * war gesetzt, sonst wären wir gar nicht in diesem Versprechen. Steht der
     * neue Arbeiter nach 15 s noch auf `installing` – er lädt die Dateien
     * vor, im Zug dauert das länger –, kam trotzdem die grüne Meldung „Du
     * hast schon den neuesten Stand", und Sekunden später schob sich das Band
     * „Eine neue Fassung liegt bereit" ins Bild. Zwei Auskünfte, die einander
     * widersprechen, innerhalb einer halben Minute.
     */
    window.setTimeout(() => {
      aufraeumen();
      fertig(anmeldung?.waiting != null ? 'neu' : 'laedt');
    }, 15_000);
  });
}

/**
 * Die bereitliegende Fassung anwenden: Wartenden Arbeiter übernehmen lassen,
 * dann neu laden.
 *
 * Nicht mehr über `updateSW(true)` aus `virtual:pwa-register`: Das wartet auf
 * das `controlling`-Ereignis von workbox-window und lädt erst dann. Auf iOS
 * (installierte App wie Safari-Tab) kommt dieses Ereignis oft gar nicht oder
 * erst beim nächsten Start – der Knopf „Aktualisieren“ tat dann scheinbar
 * nichts. Hier wird auf `controllerchange` gewartet UND nach kurzer Frist
 * ohnehin neu geladen: Der neue Arbeiter hat `skipWaiting` dann längst
 * bekommen, und ein Neuladen holt die Seite durch ihn.
 */
export function neueFassungAnwenden(
  frist = 2500,
  neuLaden: () => void = () => window.location.reload(),
): void {
  let geladen = false;
  const einmal = () => {
    if (geladen) return;
    geladen = true;
    neuLaden();
  };
  const wartend = anmeldung?.waiting;
  if (!wartend || !('serviceWorker' in navigator)) {
    einmal();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', einmal, { once: true });
  // Wird der Wartende aktiv, ohne dass die Seite ihn als Steuerer meldet
  // (iOS), genügt das auch.
  wartend.addEventListener('statechange', () => {
    if (wartend.state === 'activated') einmal();
  });
  wartend.postMessage({ type: 'SKIP_WAITING' });
  window.setTimeout(einmal, frist);
}
